import { VCryptoAlgorithm } from '@datarock/protobuffers';
import { useEffect, useRef, useState } from 'react';
import { RSA } from '@datarock/cryptography';
import { createGlobalState } from 'react-hooks-global-state';
import { getServerPublicKeyEndpoint } from '@datarock/api-client';
import { useController } from '@data-client/react';
import useDeviceId from './useDeviceId';
import useSessionId from './useSessionId';
import { withRetry } from './retryUtils';
const { useGlobalState } = createGlobalState({ serverPublicKey: null });
let globalState = {
    key: null,
    error: null,
    isLoading: false,
    retryCount: 0
};
let promise = null;
// Function to reset the global state and allow retries
export function resetServerPublicKey() {
    globalState = {
        key: null,
        error: null,
        isLoading: false,
        retryCount: 0
    };
    promise = null;
}
export default function useServerPublicKey() {
    const deviceId = useDeviceId();
    const [, setSessionId] = useSessionId();
    const [key, setKey] = useGlobalState('serverPublicKey');
    const [localState, setLocalState] = useState(globalState);
    const { fetch } = useController();
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;
    useEffect(() => {
        (async () => {
            var _a;
            // If we already have a key, don't make a new request
            if (key) {
                return;
            }
            if (promise) {
                return;
            }
            setLocalState((prev) => ({ ...prev, isLoading: true, error: null }));
            try {
                promise = withRetry(() => fetchRef.current(getServerPublicKeyEndpoint, { deviceId }), {
                    maxRetries: 3,
                    baseDelay: 1000,
                    maxDelay: 10000,
                    timeout: 30000
                });
                const response = await promise;
                if (response) {
                    if (((_a = response.serverPublicKey) === null || _a === void 0 ? void 0 : _a.algorithm) !== VCryptoAlgorithm.RSA_OAEP) {
                        throw new Error('Unrecognized server public key algorithm');
                    }
                    setSessionId(response.sessionId);
                    if (response.serverPublicKey) {
                        const k = await RSA.xmlToJsonWebKey(response.serverPublicKey.publicKey);
                        setKey(k);
                        setLocalState({
                            key: k,
                            error: null,
                            isLoading: false,
                            retryCount: 0
                        });
                        globalState = {
                            key: k,
                            error: null,
                            isLoading: false,
                            retryCount: 0
                        };
                    }
                    else {
                        throw new Error('Server Response had no public key');
                    }
                }
            }
            catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                setLocalState((prev) => ({
                    ...prev,
                    error,
                    isLoading: false
                }));
                globalState = {
                    ...globalState,
                    error,
                    isLoading: false
                };
            }
            finally {
                promise = null;
            }
        })();
    }, [deviceId, key, setKey, setSessionId]);
    // Throw error if there is one
    if (localState.error) {
        throw localState.error;
    }
    return localState.key || key;
}
